import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../i18n';

import { SlideOver, SlideOverFooter } from '../../../components/Shared/SlideOver';
import { Loading } from '../../../components/Shared/Loading';
import { useInvoiceStore } from '../../../store/useInvoiceStore';
import { formatMoney } from '@/utils/money';
import { defaultStandingPlan, isStandingPlanValid, type StandingPlan } from '@/utils/standingPlan';
import { formatServiceMonths, paymentIntervalLabelKey } from '../../../types/billing';
import { StandingInvoicePlanFields } from './StandingInvoicePlanFields';
import type { BillableRow } from './billableRun';

// why: typing a date fires a change per keystroke — wait until the input settles.
const PREVIEW_DELAY_MS = 300;

interface StandingInvoiceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  objectId: string;
}

/**
 * Creates the standing invoice („Dauerrechnung“, FAFD-27) of one object: the user picks the service
 * period (whole months) and the payment interval, sees the payment plan from the standing preview
 * and creates the draft. Errors of the backend (period, offer month, …) arrive as toast.
 */
export function StandingInvoiceDialog({ isOpen, onClose, objectId }: StandingInvoiceDialogProps) {
  const { t } = useTranslation(['invoices', 'common']);
  const navigate = useNavigate();
  const { previewStanding, createStanding, saving } = useInvoiceStore();

  const [plan, setPlan] = useState<StandingPlan>(() => defaultStandingPlan());
  const [row, setRow] = useState<BillableRow | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPlan(defaultStandingPlan());
      setRow(null);
    }
  }, [isOpen]);

  const planValid = isStandingPlanValid(plan);
  const { from, to, interval } = plan;

  useEffect(() => {
    if (!isOpen || !planValid) {
      setRow(null);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setLoadingPreview(true);
      const rows = await previewStanding({ from, to, interval });
      if (cancelled) return;
      setRow(rows?.find((r) => r.objectId === objectId) ?? null);
      setLoadingPreview(false);
    }, PREVIEW_DELAY_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      setLoadingPreview(false);
    };
  }, [isOpen, planValid, from, to, interval, objectId, previewStanding]);

  const blockers = row
    ? [
        row.alreadyInvoiced && t('invoices:standingDialog.blockers.alreadyInvoiced'),
        row.overlapsBilledMonths && t('invoices:standing.overlapsBilledMonthsHint'),
        row.startsBeforeOffer && t('invoices:standing.startsBeforeOfferHint'),
        (row.needsReviewCount ?? 0) > 0
          && t('invoices:billable.needsReview', { count: row.needsReviewCount ?? 0 }),
      ].filter((b): b is string => typeof b === 'string')
    : [];

  const canCreate = planValid && !!row && !loadingPreview && blockers.length === 0 && !saving;

  const handleCreate = async () => {
    if (!canCreate) return;
    const id = await createStanding(objectId, plan);
    if (id) {
      onClose();
      navigate(`/invoices/${id}`);
    }
  };

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={t('invoices:standingDialog.title')}
      size="lg"
      footer={
        <SlideOverFooter
          onCancel={onClose}
          onConfirm={handleCreate}
          confirmText={t('invoices:standingDialog.create')}
          cancelText={t('common:actions.cancel')}
          confirmDisabled={!canCreate}
          loading={saving}
        />
      }
    >
      <div className="space-y-5">
        <p className="text-[12px] text-slate-500">{t('invoices:standingDialog.intro')}</p>

        <StandingInvoicePlanFields value={plan} onChange={setPlan} disabled={saving} idPrefix="standing-dialog" />

        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
          <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            {t('invoices:standingDialog.preview')}
          </div>

          {!planValid ? (
            <p className="text-[13px] text-slate-500">{t('invoices:standingPlan.invalidHint')}</p>
          ) : loadingPreview ? (
            <Loading />
          ) : !row ? (
            <p className="text-[13px] text-slate-500">{t('invoices:standingDialog.notBillable')}</p>
          ) : (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-[13px]">
              <div>
                <dt className="text-[11px] text-slate-500">{t('invoices:standing.period')}</dt>
                <dd className="text-slate-900 tabular-nums">
                  {formatServiceMonths(row.serviceDateFrom, row.serviceDateTo) ?? '—'}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] text-slate-500">{t('invoices:standingDialog.regularPayment')}</dt>
                <dd className="text-slate-900 font-medium tabular-nums">
                  {formatMoney(row.totalNet)}
                  <span className="ml-1 font-normal text-slate-500">
                    {t(paymentIntervalLabelKey(row.paymentInterval))}
                  </span>
                </dd>
              </div>
              {row.adjustedPaymentNet != null && (
                <div className="sm:col-span-2">
                  <dt className="text-[11px] text-slate-500">{t('invoices:standingDialog.adjustedPayment')}</dt>
                  <dd className="text-slate-900 tabular-nums">{formatMoney(row.adjustedPaymentNet)}</dd>
                  <dd className="text-[11px] text-slate-500">{t('invoices:standingPlan.adjustedHint')}</dd>
                </div>
              )}
              <div className="sm:col-span-2">
                <dt className="text-[11px] text-slate-500">{t('invoices:standing.termNet')}</dt>
                <dd className="text-[15px] font-semibold text-slate-900 tabular-nums">{formatMoney(row.termNet)}</dd>
              </div>
            </dl>
          )}

          {blockers.length > 0 && (
            <ul className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 space-y-1">
              {blockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </SlideOver>
  );
}
